/**
 * Workbench commands and the keys that run them.
 *
 * Keys are written the way CodeMirror writes them ("Mod-Shift-p"), where Mod
 * is Cmd on macOS and Ctrl elsewhere. The listener runs in the capture phase
 * on the window, before CodeMirror and before the browser's default, so a
 * workbench key wins over an editor key with the same name.
 */

export interface Command {
    id: string;
    title: string;
    /** Shown in the palette before the title, as VS Code shows "idfweb: Build". */
    category?: string;
    keys?: string[];
    run: () => unknown;
    /** Hidden from the palette when this returns false. */
    enabled?: () => boolean;
}

/** Keys that a part of the workbench handles itself, such as the editor, listed with the commands' keys. */
export interface KeyInfo {
    title: string;
    category?: string;
    keys: string[];
}

export const IS_MAC = /Mac|iPhone|iPad/.test(navigator.platform);
const IS_WINDOWS = /Win/.test(navigator.platform);

export class CommandRegistry {
    readonly #commands = new Map<string, Command>();
    readonly #described: KeyInfo[] = [];
    readonly #listeners = new Set<() => void>();
    readonly #ran = new Set<(id: string) => void>();

    register(...commands: Command[]): void {
        for (const command of commands) this.#commands.set(command.id, command);
        this.changed();
    }

    /** Add keys that no command here runs, for the keyboard shortcuts list. */
    describe(...keys: KeyInfo[]): void {
        this.#described.push(...keys);
        this.changed();
    }

    /**
     * Every key, for the keyboard shortcuts list: the commands' keys, then
     * the described keys that no command's key hides, since the commands'
     * listener runs first.
     */
    bindings(): KeyInfo[] {
        const commands = [...this.#commands.values()].filter((command) => (command.keys?.length ?? 0) > 0);
        const taken = new Set(commands.flatMap((command) => command.keys!.map(normalizeKey)));
        const described = this.#described
            .map((info) => ({ ...info, keys: info.keys.filter((key) => !taken.has(normalizeKey(key))) }))
            .filter((info) => info.keys.length > 0);
        return [...commands.map(({ title, category, keys }) => ({ title, category, keys: keys! })), ...described];
    }

    /** Call `listener` when commands or keys change; returns a function that stops it. */
    onChange(listener: () => void): () => void {
        this.#listeners.add(listener);
        return () => this.#listeners.delete(listener);
    }

    /** Tell the listeners that commands or their keys changed. */
    changed(): void {
        for (const listener of this.#listeners) listener();
    }

    get(id: string): Command | undefined {
        return this.#commands.get(id);
    }

    all(): Command[] {
        return [...this.#commands.values()].filter((command) => command.enabled?.() ?? true);
    }

    async execute(id: string): Promise<unknown> {
        const command = this.#commands.get(id);
        if (command === undefined) throw new Error(`no command ${id}`);
        return await this.run(command);
    }

    /** Run a command, from a key, the palette or a caller, and tell the `onRun` listeners. */
    run(command: Command): unknown {
        for (const listener of this.#ran) listener(command.id);
        return command.run();
    }

    /** Call `listener` with each command's id as it starts to run; returns a function that stops it. */
    onRun(listener: (id: string) => void): () => void {
        this.#ran.add(listener);
        return () => this.#ran.delete(listener);
    }

    /** Start listening for the commands' keys on `target`. */
    listen(target: Window): void {
        target.addEventListener(
            "keydown",
            (event) => {
                // A modal dialog, such as the page's welcome, keeps the keys for itself.
                if (document.querySelector("dialog[open]:modal") !== null) return;
                for (const command of this.#commands.values()) {
                    if (!command.keys?.some((key) => matches(key, event))) continue;
                    if (command.enabled?.() === false) continue;
                    event.preventDefault();
                    event.stopPropagation();
                    void this.run(command);
                    return;
                }
            },
            { capture: true },
        );
    }
}

/** Whether a keydown is the key "Mod-Shift-p". */
export function matches(
    key: string,
    event: Pick<KeyboardEvent, "key" | "code" | "ctrlKey" | "metaKey" | "altKey" | "shiftKey">,
): boolean {
    const parts = key.split("-");
    let name = parts.pop()!;
    if (name === "" && key.endsWith("-")) name = "-";
    const want = { ctrl: false, meta: false, alt: false, shift: false };
    for (const part of parts) {
        if (part === "Mod") want[IS_MAC ? "meta" : "ctrl"] = true;
        else if (part === "Ctrl") want.ctrl = true;
        else if (part === "Cmd" || part === "Meta") want.meta = true;
        else if (part === "Alt") want.alt = true;
        else if (part === "Shift") want.shift = true;
    }
    if (
        event.ctrlKey !== want.ctrl ||
        event.metaKey !== want.meta ||
        event.altKey !== want.alt ||
        event.shiftKey !== want.shift
    ) {
        return false;
    }
    if (name.length === 1) {
        if (event.key.toLowerCase() === name.toLowerCase()) return true;
        // A Latin `key` is the layout's own letter (Ctrl+Z on AZERTY is code KeyW). Other
        // keys, such as Cyrillic or Alt's "π", fall back to the physical key in `code`.
        if (/^[a-z0-9]$/i.test(event.key)) return false;
        const code = /^[a-z]$/i.test(name)
            ? `Key${name.toUpperCase()}`
            : /^\d$/.test(name)
              ? `Digit${name}`
              : name === "-"
                ? "Minus"
                : undefined;
        return event.code === code;
    }
    return event.key === name;
}

/** "Mod-Shift-p" as a person reads it on this platform: "⇧⌘P" or "Ctrl+Shift+P". */
export function keyLabel(key: string): string {
    const parts = key.split("-");
    let name = parts.pop()!;
    if (name === "" && key.endsWith("-")) {
        parts.pop();
        name = "-";
    }
    const label = name.length === 1 ? name.toUpperCase() : name.replace(/^Arrow/, "");
    if (IS_MAC) {
        const symbols: Record<string, string> = { Ctrl: "⌃", Alt: "⌥", Shift: "⇧", Mod: "⌘", Cmd: "⌘" };
        const order = ["Ctrl", "Alt", "Shift", "Mod", "Cmd"];
        const mods = order.filter((mod) => parts.includes(mod)).map((mod) => symbols[mod]);
        return mods.join("") + label;
    }
    const names: Record<string, string> = { Mod: "Ctrl", Cmd: "Meta" };
    return [...parts.map((part) => names[part] ?? part), label].join("+");
}

/**
 * A key in one spelling, so that two spellings of it compare equal: "Shift-Mod-k"
 * and "Mod-Shift-k" are both "Ctrl-Shift-k" on Linux, and CodeMirror's "Alt-A"
 * is "Shift-Alt-a".
 */
export function normalizeKey(key: string): string {
    const parts = key.split("-");
    let name = parts.pop()!;
    if (name === "" && key.endsWith("-")) name = "-";
    const mods = new Set(
        parts.map((part) => (part === "Mod" ? (IS_MAC ? "Cmd" : "Ctrl") : part === "Meta" ? "Cmd" : part)),
    );
    if (name.length === 1 && name !== name.toLowerCase()) {
        mods.add("Shift");
        name = name.toLowerCase();
    }
    // VS Code's order, which `keyLabel` keeps outside macOS: "Ctrl+Shift+Alt+K".
    return [...["Ctrl", "Shift", "Alt", "Cmd"].filter((mod) => mods.has(mod)), name].join("-");
}

/** A CodeMirror key binding, as far as the shortcuts list reads it. */
export interface KeymapEntry {
    key?: string;
    mac?: string;
    win?: string;
    linux?: string;
    run?: unknown;
    shift?: unknown;
}

/**
 * The keys a CodeMirror keymap binds to each of the `named` commands on this
 * platform. A key bound twice runs the first binding, so only that one is kept.
 */
export function keymapKeys(
    keymap: readonly KeymapEntry[],
    named: readonly (readonly [string, unknown])[],
    category: string,
): KeyInfo[] {
    const taken = new Set<string>();
    const found = new Map<unknown, string[]>();
    for (const binding of keymap) {
        const key = (IS_MAC ? binding.mac : IS_WINDOWS ? binding.win : binding.linux) ?? binding.key;
        if (key === undefined) continue;
        for (const [run, spelled] of [
            [binding.run, key],
            [binding.shift, `Shift-${key}`],
        ] as const) {
            const normal = normalizeKey(spelled);
            if (run === undefined || taken.has(normal)) continue;
            taken.add(normal);
            found.set(run, [...(found.get(run) ?? []), normal]);
        }
    }
    return named
        .map(([title, run]) => ({ title, category, keys: found.get(run) ?? [] }))
        .filter((info) => info.keys.length > 0);
}
